import { Component, Input, OnDestroy, inject, input,effect, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { AwardsService } from '../awards.service';
import { CfiMatTableComponent } from '@k12/tables';
import type { ColumnDefinition } from '@k12/tables';
import { AwardRecipient, ProgramType, AcademicYear, ToastMessageType } from '@k12/models';
import { Subject, takeUntil, finalize } from 'rxjs';
import { LoadingSpinnerService } from '@k12/app-services';
import { ToastMessageService } from '@k12/app-services';
import { errorToastValue } from '@k12/app-services';
import { HttpErrorResponse } from '@angular/common/http';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-award-history-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    CfiMatTableComponent
  ],
  templateUrl: './award-history-form.component.html',
  styleUrls: ['./award-history-form.component.scss']
})
export class AwardHistoryFormComponent implements OnDestroy {
  private readonly awardService = inject(AwardsService);
  private readonly destroy$ = new Subject<void>();
  private readonly spinnerService = inject(LoadingSpinnerService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  public academicYear = input.required<AcademicYear | null>();
  public programType = input.required<string>();
  @Input() idPrefix: string = '';
  

  fb = inject(FormBuilder);
  awardsService = inject(AwardsService);

  awardHistoryForm: FormGroup = this.fb.group({
    round: ['', Validators.required]
  });
  rounds: Array<{ id: number; awardRoundTitle: string; createdDate: Date | string }> = [];
  recipients: AwardRecipient[] = [];
  displayedColumns: string[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  pageSize = 25;
  currentPage = 1;
  totalCount = 0;
  sortColumn = 'applicationDate';
  sortDirection = 'asc';
  pageSizeOptions = [10, 25, 50, 100];

  constructor() {
    effect(() => {
      const academicYear = this.academicYear();
      const programType = this.programType();

      if (academicYear) {
        this.updateColumnDefinitions(programType);
        this.loadAwardHistoryRounds(academicYear.id, programType);
        this.recipients = [];
        this.totalCount = 0;
        this.pageSize = 25;
        this.currentPage = 1;
      }
    });
  }

  private updateColumnDefinitions(programType: string): void {
    const baseColumns: ColumnDefinition[] = [
      { name: 'pid', header: 'PID', sortable: true, width: 12 },
      { name: 'firstName', header: 'First Name', sortable: true, width: 12 },
      { name: 'lastName', header: 'Last Name', sortable: true, width: 12 }
    ];

    const tierColumn: ColumnDefinition = { name: 'tier', header: 'Tier', sortable: true, width: 12 };
    const esaFundingLevelColumn: ColumnDefinition = {
      name: 'esaFundingLevel',
      header: 'ESA+ Funding Level',
      sortable: true,
      width: 12
    };

    const commonColumns: ColumnDefinition[] = [
      { name: 'applicationDate', header: 'Application Date', sortable: true, width: 16 },
      { name: 'lotteryNumber', header: 'Lottery Number', sortable: true, width: 12 },
      { name: 'awardAmount', header: 'Award Amount', sortable: true, width: 12 }
    ];

    const isESA = programType === ProgramType.ESA;
    this.columnDefinitions = [...baseColumns, isESA ? esaFundingLevelColumn : tierColumn, ...commonColumns];

    this.displayedColumns = this.columnDefinitions.map((col) => col.name);
  }
  private loadAwardHistoryRounds(academicYearId: number, programType: string): void {
    this.spinnerService.showSpinner();
    const programTypeId = programType === ProgramType.ESA ? 1 : 2;
    this.awardService
      .getAwardHistoryRounds(academicYearId, programTypeId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .subscribe({
        next: (data) => {
          const fetchedRounds = data
            .filter((r) => r !== null && r !== undefined)
            .map((r, idx: number) => ({
              id: r.id,
              awardRoundTitle: r.title ?? `Round ${idx + 1}`,
              createdDate: r.createdDate
            }));
          this.rounds = fetchedRounds;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(err));
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(page: number = 1): void {
    if (this.awardHistoryForm.valid) {
      const programTypeId = this.programType() === ProgramType.ESA ? 1 : 2;
      const selectedRound = this.awardHistoryForm.value.round;

      this.currentPage = page;
      this.spinnerService.showSpinner();

      this.awardsService
        .getAwardRecipientsByRound(
          this.academicYear()?.id ?? 0,
          programTypeId,
          selectedRound,
          page,
          this.pageSize,
          this.sortColumn,
          this.sortDirection
        )
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe({
          next: (response) => {
            // Handle null response
            if (!response) {
              this.recipients = [];
              this.totalCount = 0;
              return;
            }
            // service now normalizes to { data, totalCount, pageNumber, pageSize }
            this.recipients = response.data || [];
            this.totalCount = response.totalCount || this.recipients.length;
            this.pageSize = response.pageSize || this.pageSize;
            this.currentPage = response.pageNumber || this.currentPage;
            // Ensure numeric values
            this.totalCount = Number(this.totalCount) || this.recipients.length;
            this.pageSize = Number(this.pageSize) || 25;
            this.currentPage = Number(this.currentPage) || 1;
            this.cdr.markForCheck();
          },
          error: (err: HttpErrorResponse) => {
            this.recipients = [];
            this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(err));
            this.cdr.markForCheck();
          }
        });
    }
  }

  onPageChange(event: PageEvent): void {
    // Update page size when user changes it on the paginator
    if (event.pageSize && event.pageSize !== this.pageSize) {
      this.pageSize = event.pageSize;
    }
    this.onSearch(event.pageIndex + 1);
  }

  onSortChange(sort: unknown): void {
    const typedSort = sort as Sort;
    this.sortColumn = typedSort.active;
    this.sortDirection = typedSort.direction || 'asc';
    this.onSearch(1);
  }
}
